import { apiError } from "../../../../../../../../utils/formErrors";
import React, { useCallback, useEffect, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { Box, CircularProgress, MenuItem, Stack, TextField } from "@mui/material";
import BtnPrimary from "../../../../../../../../component/PrimaryButton";
import EmptyState from "../../../../../../../../component/EmptyState";
import ChecklistView from "../../../../../../../../component/onboarding/ChecklistView";
import { useNotify } from "../../../../../../../../component/NotificationProvider";
import {
  getChecklistFor,
  assignChecklist,
  updateChecklistItem,
  getOnboardingTemplates,
} from "../../../../../../../../services/onboarding";
import { tokens, radius } from "../../../../../../../../theme";

/**
 * One person's onboarding, from the admin side.
 *
 * The same list the employee sees, with the admin's powers: settle anything,
 * or waive with a reason. Assigning is here rather than only at hire because
 * people are hired before a template exists, change from Remote to Office, or
 * need starting again — and an admin looking at somebody's record is exactly
 * where that thought occurs.
 */
const ProfileOnboarding = () => {
  const { employeeId } = useParams();
  const [employeeData] = useOutletContext() || [];
  const notify = useNotify();

  const [checklist, setChecklist] = useState(null);
  const [progress, setProgress] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyItemId, setBusyItemId] = useState(null);
  const [assigning, setAssigning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, tpl] = await Promise.all([
        getChecklistFor(employeeId).catch(() => null),
        getOnboardingTemplates().catch(() => null),
      ]);
      // a 404 is the ordinary case for anyone hired before templates existed,
      // not an error worth shouting about
      setChecklist(res?.success === false ? null : res?.data || null);
      setProgress(res?.data?.progress || null);
      setTemplates(Array.isArray(tpl?.data) ? tpl.data : []);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => { load(); }, [load]);

  const onUpdate = async (item, input) => {
    setBusyItemId(item._id);
    try {
      const res = await updateChecklistItem(checklist._id, item._id, input);
      if (res?.success === false) throw apiError(res);
      setChecklist(res?.data?.checklist || checklist);
      setProgress(res?.data?.progress || progress);
    } catch (err) {
      notify.error(err?.message || "Couldn't update that item");
      load();
    } finally {
      setBusyItemId(null);
    }
  };

  const assign = async () => {
    setAssigning(true);
    try {
      const res = await assignChecklist(employeeId, templateId || undefined);
      if (res?.success === false) throw apiError(res);
      notify.success(checklist ? "Checklist replaced" : "Checklist assigned");
      await load();
    } catch (err) {
      notify.error(err?.message || "Couldn't assign a checklist");
    } finally {
      setAssigning(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ padding: "2rem", textAlign: "center" }}>
        <CircularProgress size={24} sx={{ color: tokens.textMuted }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        background: tokens.card,
        border: `1px solid ${tokens.border}`,
        borderRadius: `${radius.card}px`,
        padding: "1.25rem",
      }}
    >
      {checklist ? (
        <>
          <ChecklistView
            checklist={checklist}
            progress={progress}
            viewerRole="admin"
            onUpdate={onUpdate}
            busyItemId={busyItemId}
          />
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: "1.5rem", pt: "1rem", borderTop: `1px solid ${tokens.border}` }}>
            <TextField
              select size="small" label="Start again with" sx={{ minWidth: "14rem" }}
              value={templateId} onChange={(e) => setTemplateId(e.target.value)}
            >
              <MenuItem value="">Whichever applies</MenuItem>
              {templates.map((t) => <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>)}
            </TextField>
            <BtnPrimary onClick={assign} disabled={assigning} sx={{ paddingInline: "1rem" }}>
              {assigning ? "Working…" : "Reassign"}
            </BtnPrimary>
            {/* Replacing is destructive — say so before, not after */}
            <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
              Replaces the current checklist and everything ticked on it
            </Box>
          </Stack>
        </>
      ) : (
        <>
          <EmptyState
            title="No onboarding checklist"
            description={
              templates.length
                ? `${employeeData?.fname || "This employee"} was hired before a template applied, or never had one assigned.`
                : "No onboarding template exists yet. Create one in Settings → Onboarding, and new hires will get a checklist automatically."
            }
          />
          {templates.length > 0 && (
            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center" sx={{ mt: "1rem" }}>
              <TextField
                select size="small" label="Template" sx={{ minWidth: "14rem" }}
                value={templateId} onChange={(e) => setTemplateId(e.target.value)}
              >
                <MenuItem value="">Whichever applies</MenuItem>
                {templates.map((t) => <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>)}
              </TextField>
              <BtnPrimary onClick={assign} disabled={assigning} sx={{ paddingInline: "1rem" }}>
                {assigning ? "Working…" : "Assign a checklist"}
              </BtnPrimary>
            </Stack>
          )}
        </>
      )}
    </Box>
  );
};

export default ProfileOnboarding;
